'use client'

import './globals.css'
import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { Providers } from './providers'
import { Header } from '@/components/layout/Header'
import { Sidebar, SidebarProvider, useSidebar } from '@/components/layout/Sidebar'
import { MobileSidebar } from '@/components/layout/MobileSidebar'
import { PointerGlow, VisualPattern } from '@/components/brand'
import { ApproverShell } from '@/components/approver/ApproverShell'
import { isPanelistOnly } from '@/lib/panel-view'
import { Funnel_Display, Geologica } from 'next/font/google'
import { cn } from '@/lib/utils'
import { AUTO_NAME, ROOT_LABEL, segmentLabel } from '@/lib/navigation'
import { appTitle, documentTitle } from '@/lib/page-title'

// Configure Funnel Display (Primary / Headings)
const funnel = Funnel_Display({
  subsets: ['latin'],
  variable: '--font-funnel',
  display: 'swap',
})

// Configure Geologica (Secondary / Body)
const geologica = Geologica({
  subsets: ['latin'],
  variable: '--font-geologica',
  display: 'swap',
})

// Routes that should NOT show the main app shell (sidebar, header)
const AUTH_ROUTES = ['/auth/signin', '/auth/register', '/auth/error']

// Public, unauthenticated candidate-facing routes (golden demo flow). Rendered
// entirely outside <Providers> — no NextAuth SessionProvider (no session
// polling from an anonymous visitor), no AI Manager, no command palette, no
// sidebar/header. None of the apply/voice components use any of that context.
const PUBLIC_ROUTES = ['/apply']

function RootShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  if (PUBLIC_ROUTES.some((route) => pathname?.startsWith(route))) {
    return (
      <main className='relative z-10 flex min-h-screen items-center justify-center p-4'>
        {children}
      </main>
    )
  }
  return (
    <Providers>
      <SidebarProvider>
        <LayoutContent>{children}</LayoutContent>
      </SidebarProvider>
    </Providers>
  )
}

// Inner layout that can access sidebar context and pathname
function LayoutContent({ children }: { children: React.ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isCollapsed } = useSidebar()
  const pathname = usePathname()
  const { data: session } = useSession()

  // Check if current route is an auth route (should be full-screen)
  const isAuthRoute = AUTH_ROUTES.some((route) => pathname?.startsWith(route))

  // Plan 03: the tab names the page ("Coworker Hub · Driver Recruiting Command
  // Center | Supervity"), in the same words as the sidebar and breadcrumb.
  useEffect(() => {
    if (!isAuthRoute) document.title = documentTitle(pathname, segmentLabel, ROOT_LABEL, AUTO_NAME)
  }, [pathname, isAuthRoute])

  // Auth routes get a clean, full-screen layout
  if (isAuthRoute) {
    return (
      // No background of its own: sign-in shows the same body page wash as the
      // app (plan 03 CHG-16), instead of the old flat slate-to-white gradient.
      <div className='min-h-screen'>
        <VisualPattern variant='subtle' />
        <main className='relative z-10 flex min-h-screen items-center justify-center p-4'>
          {children}
        </main>
      </div>
    )
  }

  // Plan 09 C09-19, D09-12: a panelist-only session gets a header bar with
  // Sign out and nothing else (no sidebar, header, Ask Ava or app navigation).
  // The middleware keeps it on the "Panel reviews happen by email" page. The
  // bar never shows the signed-in person's name (Plan 09 override).
  if (isPanelistOnly(session?.roles)) {
    return (
      <>
        <VisualPattern variant='subtle' />
        <div className='relative z-10'>
          <ApproverShell product='Meridian Freight Lines'>{children}</ApproverShell>
        </div>
      </>
    )
  }

  // Normal app layout with sidebar and header
  return (
    <>
      {/* Ambient visual pattern */}
      <VisualPattern variant='subtle' />

      {/* Plan 03 CHG-17: the pointer glow tells a room where the presenter's
          pointer is. Signed-in shell only: never on PUBLIC_ROUTES or AUTH_ROUTES.
          It sits at z 40, under the header, sidebar, dialogs and toasts. */}
      <PointerGlow />

      {/* Desktop Sidebar */}
      <Sidebar />

      {/* Mobile Sidebar */}
      <MobileSidebar
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />

      {/* Main content area */}
      <div
        className={cn(
          'min-h-screen',
          // Offset for fixed sidebar - responds to collapse state
          isCollapsed ? 'md:pl-16' : 'md:pl-64',
          // Transition for sidebar collapse
          'transition-all duration-300 ease-out'
        )}
      >
        {/* Floating Header */}
        <Header onOpenMobileMenu={() => setMobileMenuOpen(true)} />

        {/* Main content with landmark role */}
        <main
          id='main-content'
          role='main'
          aria-label='Main content'
          className={cn(
            'flex flex-col',
            // Account for floating header (h-14 + top-4 + gap)
            'pt-24',
            // Padding
            'px-4 pb-8 lg:px-8',
            // Min height for full viewport
            'min-h-screen'
          )}
          tabIndex={-1}
        >
          {children}
        </main>
      </div>
    </>
  )
}

export default function RootLayout({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <html lang='en' className='light' suppressHydrationWarning>
      <head>
        <title>{appTitle(AUTO_NAME)}</title>
        <meta name='description' content='Where Intelligence Meets Human' />
      </head>
      <body
        className={cn(
          'min-h-screen font-sans antialiased',
          'bg-background text-foreground',
          funnel.variable,
          geologica.variable
        )}
      >
        <RootShell>{children}</RootShell>
      </body>
    </html>
  )
}
